// Undo/redo and version history for the page editor.
//
// Undo and redo live in memory for this session, exactly as in Orbit: the hook
// watches `page` (the single source of truth) and, after a short debounce,
// commits each change as one undo step. ⌘Z / ⌘⇧Z (⌘Y) drive them, except while
// typing in a field, where the browser's own text undo wins.
//
// Versions live on the server (GET/POST /api/pages/:id/versions). The server
// records "Published", "Unpublished" and "Saved" (on a save, at most one per 10
// minutes) itself; the editor posts "Before AI edit" and "Before restore". A
// restore is a normal edit, so ⌘Z brings the page back.
import { useCallback, useEffect, useReducer, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { syncLegacySections, type LandingPage, type PageEdits } from '../model/pages';
import type { PageVersion } from '../api';
import type { EditorApi } from './editorApi';

export type PageSnapshot = PageEdits;
export type { PageVersion };

const MAX_UNDO = 100;      // undo depth (in memory, this session)
const COMMIT_MS = 450;     // collapse a burst of edits into one undo step

const snapOf = (p: LandingPage): PageSnapshot =>
  ({ name: p.name, slug: p.slug, form_id: p.form_id ?? null, content: p.content, theme: p.theme ?? {} });

/** The page's edits as a version snapshot (steps mirrored onto content.sections). */
export function versionSnapshot(p: LandingPage): PageSnapshot {
  const s = snapOf(p);
  return { ...s, content: syncLegacySections(s.content ?? {}) };
}

/** A fingerprint of a snapshot that ignores key order (Postgres jsonb reorders
 *  keys), so a stored version can be compared with the page on screen. */
export function snapshotKey(s: PageSnapshot): string {
  const norm = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(norm);
    if (v && typeof v === 'object') {
      const out: Record<string, unknown> = {};
      for (const k of Object.keys(v as Record<string, unknown>).sort()) {
        const x = (v as Record<string, unknown>)[k];
        if (x !== undefined) out[k] = norm(x);
      }
      return out;
    }
    return v;
  };
  return JSON.stringify(norm({ name: s.name, slug: s.slug, form_id: s.form_id ?? null, content: s.content ?? {}, theme: s.theme ?? {} }));
}

export interface PageHistory {
  /** End a typing/slider burst before a discrete action such as Paste style. */
  checkpoint: () => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  /** Newest first. Null until loaded (loadVersions). */
  versions: PageVersion[] | null;
  versionsLoading: boolean;
  versionsError: string | null;
  /** Fetches the server's versions (the History panel calls it on open). */
  loadVersions: () => Promise<void>;
  /** Posts a named version of `from` (default: the page now). Best effort: a
   *  failure never blocks the edit that follows. */
  saveVersion: (label: string, from?: LandingPage) => Promise<void>;
  /** Applies a stored version as a normal edit (undo brings the page back). */
  revertTo: (v: PageVersion) => void;
}

export function usePageHistory(
  page: LandingPage | null,
  setPage: Dispatch<SetStateAction<LandingPage | null>>,
  pageId: string | undefined,
  api: Pick<EditorApi, 'listVersions' | 'saveVersion'>,
  options: {
    /** Turn a stored snapshot into editor state (normalize steps and thank-you). */
    prepare?: (snap: PageSnapshot) => PageSnapshot;
    /** False for people who can only view: nothing is posted. */
    canWrite?: boolean;
  } = {},
): PageHistory {
  const past = useRef<PageSnapshot[]>([]);
  const future = useRef<PageSnapshot[]>([]);
  const last = useRef<string | null>(null); // JSON of the last committed snapshot
  const restoring = useRef(false);          // guards undo/redo from re-recording
  const pendingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [, force] = useReducer((n: number) => n + 1, 0);
  const [versions, setVersionsState] = useState<PageVersion[] | null>(null);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState<string | null>(null);
  // Mirror of `versions` so saveVersion, called after an await from an older
  // render, prepends to the CURRENT list instead of a stale copy.
  const versionsRef = useRef<PageVersion[] | null>(null);
  const setVersions = useCallback((next: PageVersion[] | null) => { versionsRef.current = next; setVersionsState(next); }, []);
  const apiRef = useRef(api);
  const optsRef = useRef(options);
  useEffect(() => { apiRef.current = api; optsRef.current = options; });
  const loadReq = useRef(0);

  // A new page starts with empty stacks; its versions load on demand.
  useEffect(() => {
    past.current = []; future.current = []; last.current = null; restoring.current = false;
    loadReq.current += 1;
    setVersions(null);
    setVersionsError(null);
  }, [pageId, setVersions]);

  // Commit edits into the undo stack (debounced). A change made by undo/redo sets
  // `restoring` so it just re-baselines instead of pushing a new step.
  useEffect(() => {
    if (!page) return;
    const cur = JSON.stringify(snapOf(page));
    if (restoring.current) { restoring.current = false; last.current = cur; force(); return; }
    if (last.current === null) { last.current = cur; return; } // first load = baseline
    if (cur === last.current) return;
    // A new edit invalidates redo immediately, including edits still grouped by
    // the debounce. Undo may restore this pending edit before the timer fires.
    future.current = [];
    const t = setTimeout(() => {
      pendingTimer.current = null;
      if (last.current !== null && last.current !== cur) {
        past.current.push(JSON.parse(last.current) as PageSnapshot);
        if (past.current.length > MAX_UNDO) past.current.shift();
        future.current = [];
        last.current = cur;
        force();
      }
    }, COMMIT_MS);
    pendingTimer.current = t;
    return () => { clearTimeout(t); if (pendingTimer.current === t) pendingTimer.current = null; };
  }, [page]);

  const applySnap = useCallback((s: PageSnapshot) => {
    setPage((cur) => cur ? { ...cur, name: s.name, slug: s.slug, form_id: s.form_id ?? null, content: s.content, theme: s.theme ?? {} } : cur);
  }, [setPage]);

  const checkpoint = useCallback(() => {
    if (!page || optsRef.current.canWrite === false) return;
    const current = JSON.stringify(snapOf(page));
    if (last.current === null || current === last.current) return;
    if (pendingTimer.current !== null) { clearTimeout(pendingTimer.current); pendingTimer.current = null; }
    past.current.push(JSON.parse(last.current) as PageSnapshot);
    if (past.current.length > MAX_UNDO) past.current.shift();
    future.current = [];
    last.current = current;
    force();
  }, [page]);

  const undo = useCallback(() => {
    if (!page || optsRef.current.canWrite === false) return;
    const pending = last.current !== null && JSON.stringify(snapOf(page)) !== last.current;
    if (!pending && !past.current.length) return;
    if (pendingTimer.current !== null) { clearTimeout(pendingTimer.current); pendingTimer.current = null; }
    future.current.push(snapOf(page));
    restoring.current = true;
    // The pending edit belongs after the last committed snapshot. Restoring
    // that snapshot first prevents Undo from skipping the most recent state.
    applySnap(pending ? JSON.parse(last.current!) as PageSnapshot : past.current.pop()!);
    force();
  }, [page, applySnap]);

  const redo = useCallback(() => {
    if (!future.current.length || !page || optsRef.current.canWrite === false) return;
    if (last.current !== null && JSON.stringify(snapOf(page)) !== last.current) return;
    past.current.push(snapOf(page));
    restoring.current = true;
    applySnap(future.current.pop()!);
    force();
  }, [page, applySnap]);

  const loadVersions = useCallback(async () => {
    if (!pageId) return;
    const n = ++loadReq.current;
    setVersionsLoading(true);
    setVersionsError(null);
    try {
      const list = await apiRef.current.listVersions(pageId);
      if (n === loadReq.current) setVersions(Array.isArray(list) ? list : []);
    } catch (e) {
      if (n === loadReq.current) setVersionsError((e as Error).message || 'Couldn’t load the versions.');
    } finally {
      if (n === loadReq.current) setVersionsLoading(false);
    }
  }, [pageId, setVersions]);

  const saveVersion = useCallback(async (label: string, from?: LandingPage) => {
    const src = from ?? page;
    if (!src || !pageId || optsRef.current.canWrite === false) return;
    try {
      const v = await apiRef.current.saveVersion(pageId, label, versionSnapshot(src));
      if (v && versionsRef.current) setVersions([v, ...versionsRef.current.filter((x) => x.id !== v.id)]);
    } catch { /* best effort: the edit goes ahead either way */ }
  }, [page, pageId, setVersions]);

  // Revert to a stored version. Left as a normal edit (no `restoring` flag) so the
  // pre-revert state lands on the undo stack and ⌘Z brings it right back.
  const revertTo = useCallback((v: PageVersion) => {
    const snap = v.snapshot;
    if (!snap) return;
    applySnap(optsRef.current.prepare ? optsRef.current.prepare(snap) : snap);
  }, [applySnap]);

  // ⌘Z / ⌘⇧Z / ⌘Y, but never steal undo from a focused text field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      if (!meta) return;
      const k = e.key.toLowerCase();
      const isUndo = k === 'z' && !e.shiftKey;
      const isRedo = (k === 'z' && e.shiftKey) || k === 'y';
      if (!isUndo && !isRedo) return;
      const el = document.activeElement as HTMLElement | null;
      if (el && (el.isContentEditable || el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT')) return;
      e.preventDefault();
      if (isRedo) redo(); else undo();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo]);

  const pending = !!page && last.current !== null && JSON.stringify(snapOf(page)) !== last.current;
  return {
    checkpoint, undo, redo,
    canUndo: options.canWrite !== false && (pending || past.current.length > 0),
    canRedo: options.canWrite !== false && !pending && future.current.length > 0,
    versions, versionsLoading, versionsError, loadVersions, saveVersion, revertTo,
  };
}
